import { Brain, Terminal } from 'lucide-react'
import { SimpleMarkdown } from '@/frontend/components/SimpleMarkdown'
import { cn } from '@/lib/utils'
import TokensWithBreakdown from '@/frontend/pages/CommandCenter/Telemetry/components/TokensWithBreakdown'

export interface FleetSessionEvent {
  id: string
  eventId: string
  eventType: string
  parentId: string | null
  timestamp: string
  role: string | null
  model: string | null
  tokenCount: number | null
  cost: number | null
  data: Record<string, any>
}

export interface ToolCallBlock {
  id: string
  name: string
  arguments: Record<string, any>
}

export interface ExtractedContent {
  text: string
  thinking: string | null
  toolCalls: ToolCallBlock[]
  hasImages: boolean
  dataTruncated: boolean
}

// Splits OpenClaw inbound metadata prefix from the actual user message.
// Finds the last closing ``` fence in the metadata section and splits there.
function splitMetadata(text: string): { metadata: string | null; message: string } {
  if (!text.includes('(untrusted metadata)')) return { metadata: null, message: text }
  // Count all ``` positions — metadata has pairs (open+close per block)
  const fences: number[] = []
  let searchFrom = 0
  while (true) {
    const idx = text.indexOf('```', searchFrom)
    if (idx === -1) break
    fences.push(idx)
    searchFrom = idx + 3
  }
  // Need at least 4 fences (2 blocks: Conversation info + Sender) or 2 (1 block)
  if (fences.length < 2) return { metadata: null, message: text }
  // The last fence that's still part of metadata: find the last ``` before actual message content
  // Use the last even-indexed fence (closing fence of last block)
  const lastClosingIdx = fences.length % 2 === 0 ? fences[fences.length - 1] : fences[fences.length - 2]
  const splitPoint = lastClosingIdx + 3
  const metadata = text.slice(0, splitPoint).trim()
  const message = text.slice(splitPoint).trim()
  return { metadata: metadata || null, message: message || text }
}

export function extractContent(data: Record<string, any>): ExtractedContent {
  const result: ExtractedContent = { text: '', thinking: null, toolCalls: [], hasImages: false, dataTruncated: !!data?.data_truncated }
  const content = data?.message?.content
  if (!content) return result
  if (typeof content === 'string') { result.text = content; return result }
  if (!Array.isArray(content)) { result.text = JSON.stringify(content); return result }

  for (const block of content) {
    switch (block.type) {
      case 'text':
        result.text += (result.text ? '\n' : '') + block.text
        break
      case 'thinking':
        result.thinking = block.thinking
        break
      case 'toolCall':
        result.toolCalls.push({ id: block.id, name: block.name, arguments: block.arguments || {} })
        break
      case 'tool_use':
        result.toolCalls.push({ id: block.id, name: block.name, arguments: block.input || {} })
        break
      case 'image':
        result.hasImages = true
        break
    }
  }
  return result
}

export function formatTime(iso: string, _spansMultipleDays: boolean) {
  const date = new Date(iso)
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

interface MessageGroup {
  role: string | null
  events: FleetSessionEvent[]
}

export function groupEvents(events: FleetSessionEvent[]): MessageGroup[] {
  const groups: MessageGroup[] = []
  for (const event of events) {
    const lastGroup = groups[groups.length - 1]
    if (lastGroup && lastGroup.role === event.role) {
      lastGroup.events.push(event)
    } else {
      groups.push({ role: event.role, events: [event] })
    }
  }
  return groups
}

// Checks if an event would render visible content given current toggle state
export function hasVisibleContent(event: FleetSessionEvent, showToolCalls: boolean, showThinking: boolean): boolean {
  const { text, thinking, toolCalls, dataTruncated } = extractContent(event.data)
  return !!(text || (showToolCalls && toolCalls.length > 0) || (showThinking && thinking) || dataTruncated)
}

function ChatBubble({ event, sessionModel, spansMultipleDays, showLabel, showThinking, showToolCalls }: {
  event: FleetSessionEvent
  sessionModel: string | null
  spansMultipleDays: boolean
  showLabel: boolean
  showThinking: boolean
  showToolCalls: boolean
}) {
  const isUser = event.role === 'user'
  const isAssistant = event.role === 'assistant'
  const { text, thinking, toolCalls, dataTruncated } = extractContent(event.data)
  const showModel = event.model && event.model !== sessionModel
  const hasContent = text || (showToolCalls && toolCalls.length > 0) || (showThinking && thinking) || dataTruncated

  if (!hasContent) return null

  return (
    <div className={cn('flex flex-col', isAssistant ? 'items-end' : 'items-start')}>
      {showLabel && (
        <div className={cn(
          'flex items-center gap-2 mb-1',
          isAssistant ? 'flex-row-reverse' : 'flex-row'
        )}>
          <span className="text-xs font-medium text-gray-500 dark:text-muted-foreground">
            {isUser ? 'Usuario' : 'Assistente'}
          </span>
          <span className="text-xs text-gray-400 dark:text-muted-foreground font-mono">
            {formatTime(event.timestamp, spansMultipleDays)}
          </span>
          {isAssistant && event.tokenCount != null && event.tokenCount > 0 && (
            <span className="text-xs text-gray-400 dark:text-muted-foreground">
              <TokensWithBreakdown total={event.tokenCount} breakdown={event.data?.message?.usage} /> tokens
            </span>
          )}
          {showModel && (
            <span className="text-xs text-gray-400 dark:text-muted-foreground">{event.model}</span>
          )}
        </div>
      )}
      {!showLabel && (
        <span className={cn(
          'text-xs text-gray-400 dark:text-muted-foreground font-mono mb-0.5',
          isAssistant ? 'mr-1' : 'ml-1'
        )}>
          {formatTime(event.timestamp, spansMultipleDays)}
        </span>
      )}
      {hasContent && (
        <div className={cn(
          'max-w-[75%] text-sm break-words overflow-hidden',
          isUser
            ? 'bg-violet-50 dark:bg-violet-500/15 border border-violet-100 dark:border-violet-500/20 rounded-2xl rounded-tl-sm px-4 py-3'
            : 'bg-gray-100 dark:bg-muted rounded-2xl rounded-tr-sm px-4 py-2.5 text-gray-900 dark:text-foreground'
        )}>
          {dataTruncated && (
            <div className="text-xs text-gray-400 dark:text-muted-foreground italic">
              Conteudo truncado (evento excedeu limite de tamanho)
            </div>
          )}
          {isUser && text && (() => {
            const { metadata, message } = splitMetadata(text)
            return (
              <>
                {metadata && (
                  <details className="mb-2">
                    <summary className="cursor-pointer text-xs text-gray-400 dark:text-muted-foreground font-medium">
                      Contexto da mensagem
                    </summary>
                    <pre className="text-[10px] font-mono text-gray-400 dark:text-muted-foreground mt-1 whitespace-pre-wrap overflow-auto max-h-32">{metadata}</pre>
                  </details>
                )}
                {message && <div className="whitespace-pre-wrap">{message}</div>}
              </>
            )
          })()}
          {isAssistant && text && (
            <SimpleMarkdown content={text} />
          )}
          {showToolCalls && toolCalls.length > 0 && (
            <div className={cn('flex flex-wrap gap-1.5', text ? 'mt-2' : '')}>
              {toolCalls.map((tc) => (
                <details key={tc.id} className="inline open:block open:w-full">
                  <summary className="cursor-pointer inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-400">
                    <Terminal className="h-3 w-3 mr-1" /> {tc.name}
                  </summary>
                  <pre className="text-xs font-mono overflow-auto mt-2 bg-gray-900 text-gray-100 p-3 rounded-lg max-h-64">
                    {JSON.stringify(tc.arguments, null, 2)}
                  </pre>
                </details>
              ))}
            </div>
          )}
          {showThinking && thinking && (
            <details className={cn(text || toolCalls.length > 0 ? 'mt-2' : '')}>
              <summary className="cursor-pointer text-xs text-gray-400 dark:text-muted-foreground font-medium inline-flex items-center gap-1">
                <Brain className="h-3 w-3" /> Pensamento
              </summary>
              <div className="text-xs text-gray-500 dark:text-muted-foreground bg-gray-50 dark:bg-muted rounded p-3 mt-1 whitespace-pre-wrap max-h-64 overflow-auto">
                {thinking}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  )
}

// ---- tool result rendering (shared with AllEventsView) ----

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2) ?? String(v)
  } catch {
    return String(v)
  }
}

// Pretty-print when the text itself is pure JSON; otherwise keep it verbatim
// (real newlines instead of escape sequences).
function prettyToolText(text: string): string {
  const t = text.trim()
  if ((t.startsWith('{') && t.endsWith('}')) || (t.startsWith('[') && t.endsWith(']'))) {
    try {
      return JSON.stringify(JSON.parse(t), null, 2)
    } catch {
      // not valid JSON — show verbatim
    }
  }
  return text
}

// Tool outputs are usually content blocks ({type, text}), but malformed events
// can carry strings, plain objects or nothing — extract readable parts from
// ANY shape without ever throwing.
export function toolResultParts(content: unknown): string[] {
  try {
    if (content === undefined || content === null) return ['(sem conteúdo)']
    if (typeof content === 'string') return [prettyToolText(content)]
    if (Array.isArray(content)) {
      if (content.length === 0) return ['(vazio)']
      return content.map((b: any) => {
        if (typeof b === 'string') return prettyToolText(b)
        if (b && typeof b.text === 'string') return prettyToolText(b.text)
        if (b && typeof b.content === 'string') return prettyToolText(b.content)
        if (b?.type === 'image' || b?.type === 'image_url') return '[imagem]'
        return safeStringify(b)
      })
    }
    return [safeStringify(content)]
  } catch {
    return ['(conteúdo ilegível)']
  }
}

// Text wraps (no horizontal scroll); only vertical scroll past max height.
export function ToolResultBody({ content }: { content: unknown }) {
  const parts = toolResultParts(content)
  return (
    <div className="mt-2 max-h-72 space-y-2 overflow-y-auto rounded-lg bg-gray-900 p-3">
      {parts.map((p, i) => (
        <pre key={i} className="m-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere] font-mono text-xs text-gray-100">{p}</pre>
      ))}
    </div>
  )
}

// Tool OUTPUT interleaved in conversation mode (the "tool results" toggle).
// Real failures announce themselves at the HEAD of the output (or via the
// structural is_error flag). Keyword hits deep inside the text are usually
// content ABOUT errors (docs or code being read), so: only the first 600
// chars are scanned, and generic words like "error" must start a line.
const TOOL_ERROR_HEAD_PAT = new RegExp(
  [
    String.raw`^[\s>#*"'\x60\[{(-]*(?:error|exception|traceback|fatal|panic)\b`,
    String.raw`HTTP(?:\s?Error)? [45]\d\d`,
    String.raw`\b[45]\d\d (?:bad request|unauthorized|forbidden|not found|conflict|unprocessable|too many|internal server|bad gateway|service unavailable)\b`,
    String.raw`\bE(?:NOENT|ACCES|PERM|CONNREFUSED)\b`,
    String.raw`"is_?error"\s*:\s*true`,
    String.raw`"error"\s*:`,
    String.raw`\b(?:permission denied|command failed|fetch failed|request failed)\b`,
  ].join('|'),
  'im'
)

// Structural is_error flags live on the raw blocks and are lost by the text
// extraction, so check both before falling back to the keyword heuristic.
function toolResultLooksErrored(content: unknown): boolean {
  try {
    if (Array.isArray(content) &&
        content.some((b: any) => b && typeof b === 'object' && (b.is_error === true || b.isError === true))) {
      return true
    }
    return TOOL_ERROR_HEAD_PAT.test(toolResultParts(content).join('\n').slice(0, 600))
  } catch {
    return false
  }
}

function ToolResultBubble({ event, spansMultipleDays }: {
  event: FleetSessionEvent
  spansMultipleDays: boolean
}) {
  const toolName = event.data?.message?.toolName
  const content = event.data?.message?.content
  const looksErrored = toolResultLooksErrored(content)
  return (
    <div className="flex flex-col items-end">
      <details className={cn(
        'max-w-[90%] min-w-0 text-sm border border-dashed rounded-xl px-3 py-2',
        looksErrored
          ? 'border-red-300 dark:border-red-500/40 bg-red-50/60 dark:bg-red-500/10'
          : 'border-amber-300 dark:border-amber-500/40 bg-amber-50/50 dark:bg-amber-500/10'
      )}>
        <summary className={cn(
          'cursor-pointer text-xs font-medium',
          looksErrored ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'
        )}>
          Resultado: {toolName || 'ferramenta'}
          {looksErrored && <span className="ml-1.5 rounded bg-red-600 px-1 py-px text-[9px] font-semibold uppercase text-white">erro?</span>}
          <span className="ml-2 font-mono text-[10px] text-gray-400 dark:text-muted-foreground">
            {formatTime(event.timestamp, spansMultipleDays)}
          </span>
        </summary>
        <ToolResultBody content={content} />
      </details>
    </div>
  )
}

export function ConversationView({ events, sessionModel, spansMultipleDays, showThinking, showToolCalls }: {
  events: FleetSessionEvent[]
  sessionModel: string | null
  spansMultipleDays: boolean
  showThinking: boolean
  showToolCalls: boolean
}) {
  const groups = groupEvents(events)

  return (
    <div className="flex flex-col gap-5">
      {groups.map((group) => {
        if (group.role === 'toolResult') {
          return (
            <div key={group.events[0].id} className="flex flex-col gap-1.5">
              {group.events.map((event) => (
                <ToolResultBubble key={event.id} event={event} spansMultipleDays={spansMultipleDays} />
              ))}
            </div>
          )
        }

        const visibleEvents = group.events.filter(e => hasVisibleContent(e, showToolCalls, showThinking))
        if (visibleEvents.length === 0) return null

        return (
          <div key={group.events[0].id} className="flex flex-col gap-1.5">
            {visibleEvents.map((event, index) => (
              <ChatBubble
                key={event.id}
                event={event}
                sessionModel={sessionModel}
                spansMultipleDays={spansMultipleDays}
                showLabel={index === 0}
                showThinking={showThinking}
                showToolCalls={showToolCalls}
              />
            ))}
          </div>
        )
      })}
    </div>
  )
}
